// helpers/IrradianceCache.js
// Simple in-memory cache keyed by segmentId.
// This is independent of POLYGONS array indexing.
export const IRR_CACHE = new Map();

/**
 * Get cached entry (creates empty default if missing).
 */
export function getOrCreateCache(segmentId) {
  if (!segmentId) return null;

  if (!IRR_CACHE.has(segmentId)) {
    IRR_CACHE.set(segmentId, {
      tof: null,
      gti: null,
      asa: null,
      monthlyASA: null,
      irradiancePoints: null,
      displayPoints: null,
      status: { tof: "idle", gti: "idle", asa: "idle" },
      error: null,
      dirty: false,
      moveNoticeShown: false,
      needsTofGti: false,
    });
  }
  return IRR_CACHE.get(segmentId);
}

/**
 * Invalidate cached irradiance data for a segment.
 * Returns { cache, shouldNotify, hadAsa } to help UI decide on messaging.
 */
export function invalidateIrradianceForSegment(
  segmentId,
  { clearTofGti = true, clearAsa = true, markDirty = true } = {}
) {
  const cache = getOrCreateCache(segmentId);
  if (!cache) return { cache: null, shouldNotify: false, hadAsa: false };

  const hadAsa =
    cache.status?.asa === "done" ||
    cache.asa != null ||
    (Array.isArray(cache.displayPoints) && cache.displayPoints.length > 0);

  if (markDirty) cache.dirty = true;

  if (clearTofGti) {
    cache.tof = null;
    cache.gti = null;
    cache.status = { ...(cache.status || {}), tof: "idle", gti: "idle" };
    cache.needsTofGti = true;
  }

  if (clearAsa) {
    cache.asa = null;
    cache.monthlyASA = null;
    cache.irradiancePoints = null;
    cache.displayPoints = null;
    cache.status = { ...(cache.status || {}), asa: "idle" };
  }

  cache.error = null;

  const shouldNotify = hadAsa && !cache.moveNoticeShown;
  if (shouldNotify) cache.moveNoticeShown = true;

  IRR_CACHE.set(segmentId, cache);
  return { cache, shouldNotify, hadAsa };
}

/**
 * Mark segment irradiance as clean after a successful recompute.
 */
export function markIrradianceClean(segmentId) {
  const cache = getOrCreateCache(segmentId);
  if (!cache) return null;
  cache.dirty = false;
  cache.moveNoticeShown = false;
  cache.needsTofGti = false;
  IRR_CACHE.set(segmentId, cache);
  return cache;
}
